import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adEnabled, setAdEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const resp = await api.get('/auth/methods');
        if (isMounted) setAdEnabled(!!resp.data?.ad_enabled);
      } catch (e) {
        if (isMounted) setAdEnabled(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password, 'local');
    if (!result.success) { setError(result.error); setLoading(false); }
  };

  const handleAdLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password, 'ad');
    if (!result.success) { setError(result.error); setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4"
      style={{ backgroundColor: '#01158a' }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-10">
        <div
          className="flex justify-center items-center rounded-xl p-4 mb-6"
          style={{ backgroundColor: '#01158a' }}
        >
          <img src="/images/Logo-01.png" alt="VeePee Graphics" className="h-16 w-auto" />
        </div>

        <p className="text-center text-sm text-gray-500 mb-6">Sign in to your account</p>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 mb-4 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email / Username</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                required
                autoComplete="username"
                onChange={e => setEmail(e.target.value)}
                placeholder="Email or AD username"
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full border border-gray-300 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={handleAdLogin}
              disabled={loading || !adEnabled}
              className={`w-full py-2.5 text-white text-sm font-medium rounded-lg transition ${adEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'}`}
            >
              {adEnabled ? 'Sign In with Active Directory' : 'Active Directory (Not Configured)'}
            </button>
          </div>
        </form>
        <div className="mt-4 text-center">
          <a
            href="#customer-portal"
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Customer? Access the Client Portal →
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
